import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { useField, useFormikContext } from 'formik';

import Headline from '../shared/Headline';
import EmailAddress from '../fields/EmailAddress';
import Password from '../fields/Password';
import SignUpStep from './SignUpStep';
import AcceptTosCheckbox from '../fields/AcceptTosCheckbox';
import CompanyName from '../fields/CompanyName';
import isMobile from 'helpers/isMobile';
import { Button } from '@companycam/slab-web';

// #region styled-components
const StyledHeadline = styled(Headline)`
  margin-bottom: var(--cc_size_spacing_l);
`;

const FieldView = styled.div`
  margin-bottom: var(--cc_size_spacing_m);
`;
// #endregion

const signUpHeadline = (
  <FormattedMessage
    id="Onboard.signUp.title"
    description="Title for Sign Up page"
    defaultMessage="Welcome! Create an account for your company."
  />
);

const signUpHeadlineCollab = (
  <FormattedMessage
    id="Onboard.signUp.collabTitle"
    description="Title for Sign Up page when user is signing up through a collab invite"
    defaultMessage="Create your company's account to accept the collaboration."
  />
);

const signUpHeadlineMobile = (
  <FormattedMessage
    id="Onboard.signUp.mobileTitle"
    description="Title for Sign Up page when user is signing up on a mobile device"
    defaultMessage="Create an account for your company."
  />
);

const buttonText = (
  <FormattedMessage
    id="Onboard.signUp.button"
    description="Button text on sign up page"
    defaultMessage="Continue"
  />
);

const mobileButtonText = (
  <FormattedMessage
    id="Onboard.signUp.mobileButton"
    description="Mobile Button text on sign up page"
    defaultMessage="Create Account"
  />
);

const collabButtonText = (
  <FormattedMessage
    id="Onboard.signUp.collabButton"
    description="Button text on sign up page when signing up through a collab invite"
    defaultMessage="Create Company"
  />
);

const EmailPasswordCompanyName = ({
  currentStepName,
  stepName,
  buttonAction,
  projectInviteToken,
}) => {
  // Used by EmailAddress component to validate if email has already been taken
  const [emailExistsError, setEmailExistsError] = useState('');
  const [emailValidating, setEmailValidating] = useState(false);

  const [, meta, helpers] = useField('acceptedTos');
  const toggleTos = () => helpers.setValue(!meta.value);
  const { errors } = useFormikContext();
  const errorsVisible =
    errors.email || errors.password || errors.acceptedTos || emailExistsError;
  const buttonDisabled = errorsVisible || !meta.value || emailValidating;

  const renderCopy = (inviteText, mobileText, defaultText) => {
    if (projectInviteToken) {
      return inviteText;
    } else if (isMobile) {
      return mobileText;
    } else {
      return defaultText;
    }
  };

  return (
    <SignUpStep currentStepName={currentStepName} stepName={stepName}>
      <>
        <StyledHeadline
          text={renderCopy(
            signUpHeadlineCollab,
            signUpHeadlineMobile,
            signUpHeadline,
          )}
        />
        <FieldView>
          <CompanyName />
        </FieldView>
        <FieldView>
          <EmailAddress
            emailExistsError={emailExistsError}
            setEmailExistsError={setEmailExistsError}
            setEmailValidating={setEmailValidating}
          />
        </FieldView>
        <FieldView>
          <Password />
        </FieldView>
        <AcceptTosCheckbox checked={meta.value} onClickCheckbox={toggleTos} />
        <Button
          color="primary"
          style={{ width: '100%' }}
          data-testid="create-account-button"
          onClick={buttonAction}
          disabled={buttonDisabled}
        >
          {renderCopy(collabButtonText, mobileButtonText, buttonText)}
        </Button>
      </>
    </SignUpStep>
  );
};

EmailPasswordCompanyName.propTypes = {
  currentStepName: PropTypes.string,
  stepName: PropTypes.string,
  buttonAction: PropTypes.func,
  projectInviteToken: PropTypes.string,
};

export default EmailPasswordCompanyName;
